import { minBy, sumBy } from 'es-toolkit';

import { TrussEnvelope } from '@paragon/contract-library';
import {
  angleEquals,
  angleLessThan,
  Line2D,
  LineSegment2D,
  Point2D,
  Polygon2D,
  Vector2D,
} from '@paragon/geometry-library';
import { getEnvelopeSegment, getEnvelopeSegmentFromDefiningSegment } from '@paragon/layout';
import { groupTransitively, makeWeakCache, maxByMultiple, notNullish, sortBy } from '@paragon/utilities';

/**
 * Identifies runs of truss envelopes that should be dimensioned and calculates dimension segments for each run.
 *
 * @returns An array of line segments representing every dimension that needs to be drawn for the given truss envelopes.
 */
export function calculateAutomaticRunDimensions(
  dimensionedArea: Polygon2D | null,
  trussEnvelopes: readonly TrussEnvelope[],
  prohibitedAreas?: Polygon2D[],
): LineSegment2D[] {
  if (!dimensionedArea) return [];

  const trussCenterSegments = getMergedTrussCenterSegments(trussEnvelopes);
  return new RunDimensionCalculator(dimensionedArea, trussCenterSegments, { prohibitedAreas }).run();
}

interface TestSegment {
  definition: LineSegment2D;
  intersectedEnvelopeGuids: Set<string>;
}

interface DetectedRun {
  testSegments: LineSegment2D[];
  intersectedEnvelopeGuids: Set<string>;
}

const MINIMUM_RUN_LENGTH = 2;
const NTH_OF_AN_INCH_PRECISION = 16;
const MIN_DIMENSION_LENGTH_INCHES = 3;
const MAX_DIMENSION_LENGTH_INCHES = 10 * 12;
const MAX_ENVELOPE_ADJACENCY_INCHES = 4;

// for internal use; do not export
const getMergedTrussCenterSegments = makeWeakCache(prepareMergedTrussCenterSegments);

// not for direct use; see getMergedTrussCenterSegments above
function prepareMergedTrussCenterSegments(
  trussEnvelopes: readonly TrussEnvelope[],
): ReadonlyMap<string, LineSegment2D> {
  // We coalesce truss envelopes that are collinear and very near each other. This not only reduces processing time,
  //   but also reduces the number of duplicate dimensions.

  const envelopesWithCenterSegments = trussEnvelopes
    .map((envelope) => {
      const definingSegment = getEnvelopeSegment(envelope);
      if (definingSegment == null) return null;

      const centerSegment = getEnvelopeSegmentFromDefiningSegment(definingSegment, envelope, 'Center');
      const centerLine = centerSegment.toLine();

      return { guid: envelope.guid, centerSegment, centerLine };
    })
    .filter(notNullish);

  const envelopesGroupedByCollinear = groupTransitively(envelopesWithCenterSegments, (a, b) =>
    a.centerLine.equals(b.centerLine),
  );

  const envelopesGroupedByAdjacent = envelopesGroupedByCollinear.flatMap((group) =>
    groupTransitively(
      group,
      (a, b) => fastCollinearSegmentDistance(a.centerSegment, b.centerSegment) <= MAX_ENVELOPE_ADJACENCY_INCHES,
    ),
  );

  const mergedEnvelopes: [string, LineSegment2D][] = envelopesGroupedByAdjacent.map((group) => {
    if (group.length === 1) {
      return [group[0].guid, group[0].centerSegment];
    }

    const { centerLine } = group[0];

    const allEndPoints = group.flatMap((envelope) => envelope.centerSegment.endPoints);
    const allDistances = allEndPoints.map((point) => point.projectedSignedDistanceAlong(centerLine));

    const minDistance = Math.min(...allDistances);
    const maxDistance = Math.max(...allDistances);

    const minPoint = centerLine.getPointAlongLine(minDistance);
    const maxPoint = centerLine.getPointAlongLine(maxDistance);

    const newCenterSegment = new LineSegment2D(minPoint, maxPoint);

    return [group[0].guid, newCenterSegment];
  });

  return new Map<string, LineSegment2D>(mergedEnvelopes);
}

class RunDimensionCalculator {
  // Note that these fields are not private for performance reasons. They are accessed repeatedly in nested loops
  //   throughout dimension calculation.

  readonly dimensionedArea: Polygon2D;
  readonly prohibitedAreas: Polygon2D[];

  readonly envelopeGuids: readonly string[];
  readonly trussCenterSegments: ReadonlyMap<string, LineSegment2D>;

  private getTrussCenterSegment(trussEnvelopeGuid: string) {
    return this.trussCenterSegments.get(trussEnvelopeGuid)!;
  }

  constructor(
    dimensionedArea: Polygon2D,
    trussCenterSegments: ReadonlyMap<string, LineSegment2D>,
    options?: { prohibitedAreas?: Polygon2D[] },
  ) {
    this.dimensionedArea = dimensionedArea;
    this.prohibitedAreas = options?.prohibitedAreas ?? [];
    this.envelopeGuids = [...trussCenterSegments.keys()];
    this.trussCenterSegments = trussCenterSegments;
  }

  run() {
    let results: LineSegment2D[] = [];

    // We know that non-parallel envelopes will never be part of a run dimension together. Grouping as early as possible
    //   is important for performance since it greatly reduces the number of comparisons needed when developing a run.
    for (const [envelopeGuidsInGroup, envelopeGuidsNotInGroup] of this.getGroupedEnvelopeGuids()) {
      // The foundation of dimension generation is our test lines which are extended from each envelope at its end
      //   points and midpoint.
      const testLines = this.developTestLines(envelopeGuidsInGroup);

      // Using the raw test lines would generate unrealistic dimensions, so we need to bound them into test segments.
      //   Filtering out runs that are considered too short to dimension also happens here.
      const testSegments = this.developTestSegments(testLines, envelopeGuidsInGroup, envelopeGuidsNotInGroup);

      // We now have a list of line segments that span the runs of trusses in this envelope group. Combine identical
      //   and/or redundant segments together to identify unique runs.
      const detectedRuns = this.consolidateTestSegmentsIntoRuns(testSegments);

      // Now that we have identified the unique runs of trusses, generate the individual dimension segments which are
      //   the final result of this process.
      for (const detectedRun of detectedRuns) {
        results = results.concat(this.developDimensionSegments(detectedRun));
      }
    }

    return results;
  }

  /**
   * Groups the truss envelopes being dimensioned into groups of parallel envelopes.
   */
  private getGroupedEnvelopeGuids() {
    return partitionGroupWith(
      this.envelopeGuids,
      (first, second) => {
        const firstSegment = this.getTrussCenterSegment(first);
        const secondSegment = this.getTrussCenterSegment(second);

        return firstSegment.isParallelTo(secondSegment);
      },
      (guid) => orientAngleWithUp(this.getTrussCenterSegment(guid).direction.angle),
      { circular: true },
    );
  }

  /**
   * Identifies the set of test lines needed to develop dimensions for a group of truss envelopes. Test lines are drawn
   *   from the center and endpoints of envelopes in the group.
   */
  private developTestLines(envelopeGuidsInGroup: string[]): Line2D[] {
    const representativeLine = this.getTrussCenterSegment(envelopeGuidsInGroup[0]).toLine();
    const testLineDirection = representativeLine.direction.rotateQuarterTurn();

    const testLinePositions = new Set<number>();

    for (const envelope of envelopeGuidsInGroup) {
      const definitionSegment = this.getTrussCenterSegment(envelope);

      for (const testLineOrigin of definitionSegment.endPoints.concat([definitionSegment.midpoint])) {
        testLinePositions.add(
          Math.round(testLineOrigin.projectedSignedDistanceAlong(representativeLine) * NTH_OF_AN_INCH_PRECISION),
        );
      }
    }

    const testLines = [...testLinePositions].map((distance) => {
      const basePoint = representativeLine.getPointAlongLine(distance / NTH_OF_AN_INCH_PRECISION);
      return new Line2D(basePoint, testLineDirection);
    });

    return testLines;
  }

  /**
   * Creates test segments from the generated test lines. Test segments are based on test lines, but are bounded by
   *   items that would interrupt a run, such as the edges of the dimensioned area and trusses not in the run. They also
   *   include a list of truss envelopes that intersect the segment.
   */
  private developTestSegments(
    testLines: Line2D[],
    envelopeGuidsInGroup: string[],
    envelopeGuidsNotInGroup: string[],
  ): TestSegment[] {
    // Step #1: dimensions can't extend outside the dimensioned area, so the test lines need to be split at the edges.
    let lineSegments = testLines.flatMap(
      this.dimensionedArea.findSegmentIntersectionsWithLine.bind(this.dimensionedArea),
    );

    // Step #1.5: dimensions can't extend through prohibited areas (if any), unless the prohibited areas contain the
    //   centroid of a truss in the group.
    const trussCentroidsInGroup = envelopeGuidsInGroup.map((guid) => this.getTrussCenterSegment(guid).midpoint);
    const prohibitedAreas = this.prohibitedAreas.filter(
      (area) => !trussCentroidsInGroup.some((centroid) => area.containsPoint(centroid)),
    );
    if (prohibitedAreas.length > 0) {
      for (const prohibitedArea of prohibitedAreas) {
        lineSegments = lineSegments.flatMap((lineSegment) => prohibitedArea.splitSegmentAtEdges(lineSegment));
      }

      // After all the splitting, filter out any segments that are entirely within a prohibited area as these will
      //   clearly not lead to valid dimensions.
      lineSegments = lineSegments.filter(
        (lineSegment) =>
          !prohibitedAreas.some(
            (area) => area.containsPoint(lineSegment.basePoint) && area.containsPoint(lineSegment.endPoint),
          ),
      );
    }

    // Step #2: A run can't extend through a truss not in the run; for example, at the corner of a building the
    //   dimension for a run of end jacks shouldn't extend through the king jack.
    for (const envelopeGuidNotInGroup of envelopeGuidsNotInGroup) {
      const otherEnvelopeSegment = this.getTrussCenterSegment(envelopeGuidNotInGroup);

      // This loop is performance sensitive because it has the unfortunate characteristic of working with every test
      //   line and every truss envelope not in the group. Ideally (from a performance perspective) we would do this
      //   slicing after the test segments have been consolidated to dramatically reduce the number of comparisons
      //   needed.
      //
      // Every attempt at this has been unsuccessful so far, though. I've been able to optimize this approach enough
      //   that it only makes dimension generation 30% slower, which is acceptable enough for now. If you need to change
      //   this loop make sure to benchmark, and perhaps consider re-attempting the implementation described above.
      for (let i = 0; i < lineSegments.length; i++) {
        const segment = lineSegments[i];
        const intersection = otherEnvelopeSegment.intersectWithSegment(segment);

        if (intersection != null && !intersection.equals(segment.basePoint) && !intersection.equals(segment.endPoint)) {
          const splitSegment = segment.splitAtPoint(intersection);
          // Better to throw here then get caught in an infinite loop.
          if (splitSegment.length !== 2) throw new Error('Expected segment to be split into two.');
          lineSegments.splice(i, 1, ...splitSegment);
          i -= 1;
        }
      }
    }

    const testSegments = lineSegments.map((lineSegment) => ({
      definition: lineSegment,
      intersectedEnvelopeGuids: new Set<string>(),
    }));

    // Step #3: We now have the line segments needed to define our "test segments". but also need to record which truss
    //   envelopes they intersect for later reference.
    for (const envelopeGuid of envelopeGuidsInGroup) {
      const envelopeCenterSegment = this.getTrussCenterSegment(envelopeGuid);
      for (const testSegment of testSegments) {
        if (envelopeCenterSegment.intersectWithNonParallelSegment(testSegment.definition)) {
          testSegment.intersectedEnvelopeGuids.add(envelopeGuid);
        }
      }
    }

    // Step #4: Only runs with at least MINIMUM_RUN_LENGTH truss envelopes are considered worth dimensioning.
    return testSegments.filter(({ intersectedEnvelopeGuids }) => intersectedEnvelopeGuids.size >= MINIMUM_RUN_LENGTH);
  }

  /**
   * Identifies runs of trusses by combining test segments that intersect the same truss envelopes into a single run.
   */
  private consolidateTestSegmentsIntoRuns(testSegments: TestSegment[]): DetectedRun[] {
    const detectedRuns = [];

    for (const testSegment of testSegments) {
      let added = false;

      for (const existingRun of detectedRuns) {
        if (eitherIsSubset(testSegment.intersectedEnvelopeGuids, existingRun.intersectedEnvelopeGuids)) {
          existingRun.testSegments.push(testSegment.definition);
          mergeSets(existingRun.intersectedEnvelopeGuids, testSegment.intersectedEnvelopeGuids);

          added = true;
          break;
        }
      }

      if (!added) {
        detectedRuns.push({
          testSegments: [testSegment.definition],
          intersectedEnvelopeGuids: new Set<string>(testSegment.intersectedEnvelopeGuids),
        });
      }
    }

    this.mergeRedundantRuns(detectedRuns);

    return detectedRuns;
  }

  /**
   * Detects and removes redundant runs whose truss envelopes are already covered by some combination of other runs.
   */
  private mergeRedundantRuns(detectedRuns: DetectedRun[]) {
    let removedRedundantRun;

    do {
      removedRedundantRun = false;

      // Note that this function is clearly not optimized, but that hasn't proved to be a performance problem since it
      //   is only called once per group, and after consolidation has happened. Feel free to rewrite with a faster
      //   implementation if that changes.

      // This is subjective, but in my testing the resulting dimensions felt better when smaller redundant runs were
      //   eliminated first. So we sort the runs by the number of truss envelopes they cover before each pass.
      detectedRuns.sort((a, b) => a.intersectedEnvelopeGuids.size - b.intersectedEnvelopeGuids.size);

      for (const detectedRun of detectedRuns) {
        const envelopesCoveredByOtherRuns = new Set<string>();

        for (const otherRun of detectedRuns) {
          if (otherRun === detectedRun) continue;

          for (const coveredEnvelope of otherRun.intersectedEnvelopeGuids) {
            envelopesCoveredByOtherRuns.add(coveredEnvelope);
          }
        }

        if ([...detectedRun.intersectedEnvelopeGuids.values()].every((guid) => envelopesCoveredByOtherRuns.has(guid))) {
          detectedRuns.splice(detectedRuns.indexOf(detectedRun), 1);
          removedRedundantRun = true;

          break;
        }
      }
    } while (removedRedundantRun === true);
  }

  /**
   * Calculates the dimension segments needed to fully dimensions a single run. Will pick the ideal location for a
   *   dimension line based on the test segments that identified the run.
   */
  private developDimensionSegments(detectedRun: DetectedRun): LineSegment2D[] {
    const centerLinesInRun = Array.from(detectedRun.intersectedEnvelopeGuids).map((guid) =>
      this.getTrussCenterSegment(guid),
    );

    const possibleDimensionLocations = maxByMultiple(detectedRun.testSegments, (testSegment) =>
      sumBy(centerLinesInRun, (centerLine) => (centerLine.intersectWithNonParallelSegment(testSegment) ? 1 : 0)),
    );

    let averageMidpoint = new Vector2D(0, 0);
    for (const dimensionLine of possibleDimensionLocations) {
      averageMidpoint = averageMidpoint.add(dimensionLine.midpoint);
    }

    averageMidpoint = averageMidpoint.divideScalar(possibleDimensionLocations.length);
    const averageMidPoint = Point2D.fromObject(averageMidpoint);
    const distanceToMidPoint = (segment: LineSegment2D) => averageMidPoint.distanceToLineSegment(segment);

    const chosenDimensionLocation = minBy(possibleDimensionLocations, distanceToMidPoint)!;

    let dimensionSegments = [chosenDimensionLocation];

    for (const centerLine of centerLinesInRun) {
      dimensionSegments = dimensionSegments.flatMap((segment) => segment.sliceBySegment(centerLine));
    }

    if (dimensionSegments.length <= MINIMUM_RUN_LENGTH) {
      console.warn('An automatic run dimension will not be visible because the detection algorithm failed.');
      return [];
    }

    const isValidEndPoint = (point: Point2D) =>
      centerLinesInRun.some((segment) => segment.contains(point)) ||
      this.dimensionedArea.edges.some((edge) => edge.contains(point));

    const firstSegmentIsValid = dimensionSegments[0].endPoints.every(isValidEndPoint);
    const lastSegmentIsValid = dimensionSegments[dimensionSegments.length - 1].endPoints.every(isValidEndPoint);

    if (!firstSegmentIsValid) dimensionSegments = dimensionSegments.slice(1);
    if (!lastSegmentIsValid) dimensionSegments = dimensionSegments.slice(0, -1);

    return dimensionSegments.filter(
      (segment) => segment.length >= MIN_DIMENSION_LENGTH_INCHES && segment.length <= MAX_DIMENSION_LENGTH_INCHES,
    );
  }
}

/**
 * A hybrid method combining grouping and partitioning. The returned groups are in the form of `[T[], T[]]` tuples; the
 * first array being the items in the group, the second being the items that are not in the group. `sortKey` orders the
 * items before they are run through `comparator`; only adjacent items are compared. If `circular` is `true` the very
 * first and very last items are also compared and their groups may be combined.
 */
function partitionGroupWith<T>(
  items: readonly T[],
  comparator: (first: T, second: T) => boolean,
  sortKey: (item: T) => number,
  { circular }: { circular: boolean } = { circular: false },
): [T[], T[]][] {
  const result: [T[], T[]][] = [];

  const sortedItems = sortBy(items, sortKey);
  for (const item of sortedItems) {
    // Add item as a non-member in all previous groups
    for (const previousGroup of result.slice(0, -1)) {
      const [_, itemsNotInGroup] = previousGroup;
      itemsNotInGroup.push(item);
    }

    let addedToExistingGroup = false;

    // Add the item to the last group as either a member or non-member
    if (result.length !== 0) {
      const lastGroup = result.at(-1)!;
      const [itemsInGroup, itemsNotInGroup] = lastGroup;
      const lastItemInGroup = itemsInGroup.at(-1)!;
      if (comparator(item, lastItemInGroup)) {
        itemsInGroup.push(item);
        addedToExistingGroup = true;
      } else {
        itemsNotInGroup.push(item);
      }
    }

    // If not added to last group as a member, create a new group
    if (!addedToExistingGroup) {
      result.push([[item], []]);
    }
  }

  if (circular && sortedItems.length > 2 && result.length > 1) {
    // Check whether the first and last groups are combinable
    if (comparator(sortedItems[0], sortedItems.at(-1)!)) {
      // Combine the first and last groups by doing some math on the collection sizes
      const firstGroup = result[0];
      // Remove the last group since it will be combined with the first
      const lastGroup = result.pop()!;
      // Add items to the first group
      firstGroup[0].unshift(...lastGroup[0]);
      // Remove same items from first group's non-member list
      firstGroup[1].splice(firstGroup[1].length - lastGroup[0].length);
    }
  }

  return result;
}

/**
 * Returns true if the first set is a subset of the second set.
 */
function isSubset<T>(subset: Set<T>, set: Set<T>) {
  return [...subset.values()].every((value) => set.has(value));
}

/**
 * Returns true if either set is a subset of the other.
 */
function eitherIsSubset<T>(first: Set<T>, second: Set<T>) {
  return isSubset(first, second) || isSubset(second, first);
}

/**
 * Adds all the items in the second set to the first set.
 */
function mergeSets<T>(first: Set<T>, second: Set<T>) {
  for (const item of second) {
    first.add(item);
  }
}

/**
 * Calculates the distance between collinear segments. Built for speed. Assumes segments are collinear. Will return
 * nonsensical--but always negative--values if segments overlap.
 */
function fastCollinearSegmentDistance(a: LineSegment2D, b: LineSegment2D): number {
  return a.midpoint.distanceTo(b.midpoint) - (a.length + b.length) / 2;
}

/**
 * Returns the same angle, possibly flipped to be oriented with the positive Y axis. Works using fuzzy equality.
 */
function orientAngleWithUp(angle: number): number {
  if (angleLessThan(angle, 0)) return angle + 180;
  if (angleEquals(angle, 180)) return angle - 180;
  return angle;
}
